import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { FormattedMessage, injectIntl } from 'react-intl';
import { FieldArray, Field } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import Heading from '../../../common/Heading';
import formStyles from '../../../styles/forms.module.scss';
import s from '../StepWindFacilityData.module.scss';
import WindFacilityForm from '../FacilityForm/WindFacilityForm';
import AddFacilityButton from '../FacilityButtons/AddFacilityButton';
import FileUploadInput from '../../../common/FormFields/FileUploadInput';
import { ALLOWED_FILE_EXTENSIONS_WIND } from '../FormValidation/ValidationSchema';
import { AccessibleDatepickerInput } from '../../../common/FormFields';

function StepWindFacilityData(props) {
  const { values, form } = props;
  const addFacilityButtonRef = useRef(null);
  return (
    <section className={s.stepWindFacilityData}>
      <Heading level={2} className={`${s.heading} ${s.desktopHeading}`}>
        <FormattedMessage id="offerWizard.StepWindFacilityData.title.desktop" />
      </Heading>
      <Heading level={2} className={`${s.heading} ${s.mobileHeading}`}>
        <FormattedMessage id="offerWizard.StepWindFacilityData.title.mobile" />
      </Heading>
      <div
        className={`${formStyles.formFields} ${s.preventScrollOnNewFacility}`}
      >
        <div className={formStyles.inputPair}>
          <Field
            name="information.start"
            labelMsg="offerWizard.StepSomeInformation.start.label.directmarketing"
            component={AccessibleDatepickerInput}
            minDate={new Date()}
          />
          <Field
            name="information.end"
            labelMsg="offerWizard.StepSomeInformation.end.label.directmarketing"
            component={AccessibleDatepickerInput}
            minDate={() => {
              if (values.information && values.information.start) {
                return new Date(values.information.start);
              }
              return new Date();
            }}
          />
        </div>
        <hr className={formStyles.separator} />
        <FieldArray
          name="windFacilityData"
          render={(arrayHelpers) => {
            let facilityForms;
            const deleteFacility = (index) => {
              if (index === values.windFacilityData.length - 1) {
                arrayHelpers.remove(index);
                addFacilityButtonRef.current.focus();
              } else {
                arrayHelpers.remove(index);
                const idOfNextFacility = values.windFacilityData[index + 1].id;
                const nextFacilityNode = document.querySelector(
                  `[data-id="${idOfNextFacility}"]`
                );
                nextFacilityNode.focus();
              }
            };
            if (values.windFacilityData && values.windFacilityData.length) {
              facilityForms = values.windFacilityData.map(
                (data, index, array) => (
                  /* eslint-disable react/no-array-index-key */
                  <CSSTransition
                    key={data.id}
                    timeout={{
                      appear: 500,
                      enter: 500,
                      exit: 500,
                    }}
                    classNames={{
                      enter: s['facility-transition-enter'],
                      enterActive: s['facility-transition-enter-active'],
                      exit: s['facility-transition-exit'],
                      exitActive: s['facility-transition-exit-active'],
                    }}
                  >
                    <WindFacilityForm
                      id={data.id}
                      index={index}
                      array={array}
                      arrayHelpers={arrayHelpers}
                      values={values}
                      form={form}
                      deleteFacility={deleteFacility}
                    />
                  </CSSTransition>
                )
              );
            }
            return (
              <>
                <TransitionGroup>{facilityForms}</TransitionGroup>
                <AddFacilityButton
                  ref={addFacilityButtonRef}
                  onClick={() => arrayHelpers.push({ id: uuidv4() })}
                />
              </>
            );
          }}
        />
      </div>
      <div className={s.facilityDetails} id="facilityDetailsDescription">
        <legend className={s.legend}>
          <FormattedMessage id="offerWizard.StepWindFacilityData.facilityDetails" />
        </legend>
        <div>
          <FormattedMessage id="offerWizard.StepWindFacilityData.facilityDetails.text" />
        </div>
      </div>
      <div className={formStyles.inputPairSingle}>
        <Field
          name="windFacilityFile"
          component={FileUploadInput}
          allowedExtensions={ALLOWED_FILE_EXTENSIONS_WIND}
          ariaDescribedBy="facilityDetailsDescription"
        />
      </div>
    </section>
  );
}

StepWindFacilityData.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  /** Formik values */
  values: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  form: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
};

export default injectIntl(StepWindFacilityData);
